/**
 * Collaborators page.
 * Shows all connected individuals: workspace members, project collaborators, and field agents.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useWorkspaceUrl } from "../lib/routes";
import { useIsWorkspaceAdmin, useWorkspaceCollaborators, useWorkspaceMemberLabels } from "../hooks/useWorkspaceMembers";
import { collaboratorName, matchesCollaboratorSearch } from "../lib/collaborators";
import { isAgent } from "../lib/agentMembers";
import { PageHeader } from "../components/layout/PageHeader";
import { WorkspaceRoleBadge } from "../components/WorkspaceRoleBadge";
import type { UnifiedCollaborator } from "../services/naraDoor";
import {
  Users,
  Plus,
  Bot,
  Search,
  Mail,
  Phone,
  Clock,
  CheckCircle,
} from "lucide-react";
import { KiiDialogLayout } from "../components/kii";
import { InviteForm } from "../components/invite/InviteForm";
import { PendingInvitations } from "../components/invite/PendingInvitations";
import { AddAgentDialog } from "../components/agent/AddAgentDialog";
import { AgentBadge } from "../components/agent/AgentBadge";

// Helper to format phone numbers for display
function formatPhoneForDisplay(phone?: string): string {
  if (!phone) return "";
  // Simple formatting - you can enhance this as needed
  return phone;
}

export function WorkspaceMembers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeWorkspace } = useWorkspace();
  const urls = useWorkspaceUrl();
  const { data: collaboratorsData, isLoading, isError: collaboratorsFailed } = useWorkspaceCollaborators();
  const isWorkspaceAdmin = useIsWorkspaceAdmin();
  const { data: labelData, error: labelsError } = useWorkspaceMemberLabels(activeWorkspace?.id, isWorkspaceAdmin);
  const labels = isWorkspaceAdmin ? labelData ?? {} : {};

  const [searchTerm, setSearchTerm] = useState("");
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showAgentDialog, setShowAgentDialog] = useState(false);

  const collaborators = collaboratorsData?.collaborators || [];
  const filteredCollaborators = collaborators.filter((c) =>
    matchesCollaboratorSearch(c, labels[c.user_id]?.label_name, searchTerm)
  );

  const handleRowClick = (collaborator: UnifiedCollaborator) => {
    if (urls) {
      navigate(urls.collaborator(collaborator.user_id));
    }
  };

  const getStatusBadge = (status?: string) => {
    if (!status || status === "active") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
          <CheckCircle className="h-3 w-3" />
          {t("workspaceMembers.status.active")}
        </span>
      );
    }
    if (status === "invited") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300">
          <Clock className="h-3 w-3" />
          {t("workspaceMembers.status.pending")}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-muted text-muted-foreground">
        {status}
      </span>
    );
  };

  const getOnProjectsBadge = (collaborator: UnifiedCollaborator) => {
    if (!collaborator.is_on_any_project) return null;
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300 whitespace-nowrap">
        {t("workspaceMembers.badge.onProjects")}
      </span>
    );
  };

  const getCollaboratorName = (collaborator: UnifiedCollaborator) =>
    collaboratorName(collaborator, labels[collaborator.user_id]?.label_name);

  // An admin's label phone stands in only when the account has no phone of its own.
  const getPhone = (collaborator: UnifiedCollaborator) =>
    collaborator.user?.phone || labels[collaborator.user_id]?.label_phone || undefined;

  return (
    <div>
      <PageHeader
        title={t("workspaceMembers.title")}
        description={
          activeWorkspace
            ? t("workspaceMembers.description", { workspace: activeWorkspace.name })
            : t("workspaceMembers.descriptionNoWorkspace")
        }
        breadcrumbs={[{ label: t("workspaceMembers.breadcrumb.workspace") }, { label: t("workspaceMembers.breadcrumb.collaborators") }]}
        actions={
          activeWorkspace && (
            <div className="flex items-center gap-2">
              {isWorkspaceAdmin && (
                <button
                  onClick={() => setShowAgentDialog(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-border text-foreground rounded-lg hover:bg-accent transition-colors"
                >
                  <Bot className="h-4 w-4" />
                  {t("agent.add.button")}
                </button>
              )}
              <button
                onClick={() => setShowInviteDialog(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                {t("workspaceMembers.inviteButton")}
              </button>
            </div>
          )
        }
      />

      {!activeWorkspace ? (
        <div className="bg-card rounded-lg border border-border p-8 text-center">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-lg font-medium text-foreground mb-2">
            {t("workspaceMembers.noWorkspace")}
          </h2>
          <p className="text-muted-foreground">
            {t("workspaceMembers.noWorkspaceMessage")}
          </p>
        </div>
      ) : (
        <>
          <PendingInvitations workspaceId={activeWorkspace.id} />

          {labelsError && (
            <p className="mb-4 text-sm text-red-600">{t("collaboratorDetail.privateDetails.loadError")}</p>
          )}

          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <input
                type="text"
                placeholder={t("workspaceMembers.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Collaborators List */}
          {isLoading ? (
            <div className="space-y-2 lg:space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-14 lg:h-16 bg-muted rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : collaboratorsFailed ? (
            <p role="alert" className="text-sm text-destructive">{t("workspaceMembers.errors.loadFailed")}</p>
          ) : filteredCollaborators.length > 0 ? (
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              {/* Mobile: Card list */}
              <div className="sm:hidden divide-y divide-border">
                {filteredCollaborators.map((collaborator) => (
                  <div
                    key={collaborator.user_id}
                    className="p-3 cursor-pointer hover:bg-accent transition-colors"
                    onClick={() => handleRowClick(collaborator)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center shrink-0">
                          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                            {getCollaboratorName(collaborator).charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium text-foreground block truncate">
                            {getCollaboratorName(collaborator)}
                          </span>
                          <div className="flex flex-col gap-0.5 mt-1">
                            {collaborator.user?.email && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Mail className="h-3 w-3 text-muted-foreground" />
                                {collaborator.user.email}
                              </span>
                            )}
                            {getPhone(collaborator) && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Phone className="h-3 w-3 text-muted-foreground" />
                                {formatPhoneForDisplay(getPhone(collaborator))}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {getStatusBadge(collaborator.status)}
                            <WorkspaceRoleBadge role={collaborator.role} />
                            {isAgent(collaborator) && <AgentBadge />}
                            {getOnProjectsBadge(collaborator)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: Table */}
              <table className="w-full hidden sm:table">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-foreground">
                      {t("workspaceMembers.tableHeader.name")}
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-foreground hidden md:table-cell">
                      {t("workspaceMembers.tableHeader.contact")}
                    </th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-foreground w-28">
                      {t("workspaceMembers.tableHeader.status")}
                    </th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-foreground w-28">
                      {t("workspaceMembers.tableHeader.workspaceRole")}
                    </th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-foreground w-28">
                      {t("workspaceMembers.tableHeader.fieldAgent")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredCollaborators.map((collaborator) => (
                    <tr
                      key={collaborator.user_id}
                      className="hover:bg-accent cursor-pointer transition-colors"
                      onClick={() => handleRowClick(collaborator)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center shrink-0">
                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                              {getCollaboratorName(collaborator).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-foreground truncate">
                            {getCollaboratorName(collaborator)}
                          </span>
                          {isAgent(collaborator) && <AgentBadge />}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="flex flex-col">
                          <span className="flex items-center gap-1 text-sm text-foreground">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            {collaborator.user?.email || "—"}
                          </span>
                          <span className="flex items-center gap-1 text-sm text-muted-foreground mt-0.5">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            {getPhone(collaborator) ? formatPhoneForDisplay(getPhone(collaborator)) : "—"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(collaborator.status)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <WorkspaceRoleBadge role={collaborator.role} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getOnProjectsBadge(collaborator) || <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-card rounded-lg border border-border p-8 text-center">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-foreground mb-2">
                {searchTerm ? t("workspaceMembers.noMatching") : t("workspaceMembers.noCollaborators")}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm
                  ? t("workspaceMembers.adjustSearch")
                  : t("workspaceMembers.addCollaborators")}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => setShowInviteDialog(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  {t("workspaceMembers.inviteButton")}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {activeWorkspace && isWorkspaceAdmin && (
        <AddAgentDialog workspaceId={activeWorkspace.id} open={showAgentDialog} onOpenChange={setShowAgentDialog} />
      )}

      {/* Invite Collaborator Dialog */}
      {activeWorkspace && (
        <KiiDialogLayout
          open={showInviteDialog}
          onOpenChange={setShowInviteDialog}
          title={t("workspaceMembers.dialog.title")}
          description={t("invite.dialog.description")}
          size="md"
        >
          <InviteForm
            workspaceId={activeWorkspace.id}
            onCancel={() => setShowInviteDialog(false)}
            onDone={() => setShowInviteDialog(false)}
          />
        </KiiDialogLayout>
      )}
    </div>
  );
}
